/**
 * Feature flag registry — the single source of truth for which flag keys
 * are valid at compile time.
 *
 * To add a new flag:
 *   1. Create the flag in the PostHog UI with the same key.
 *   2. Add an entry below with a sensible `default` (used while PostHog is
 *      still loading and as the offline fallback) and a `description`.
 *   3. Use `useFeature('your-new-flag')` in components — typed, autocompletes,
 *      typos are a compile error.
 *   4. Flip it on (globally or per-artist group) in the PostHog dashboard.
 *
 * Deleting a flag from this registry does NOT delete it from PostHog — that
 * has to be done manually in the PostHog UI.
 */

export type FeatureFlagDefinition = {
  /** Value returned by `useFeature` when PostHog hasn't loaded or is down. */
  readonly default: boolean
  /** Human description used in PostHog and as a JSDoc reference. */
  readonly description: string
}

export const FEATURE_FLAGS = {
  moderation: {
    default: false,
    description: 'Show the Moderation + Keyword Flagging section in the sidebar nav.'
  }
} as const satisfies Record<string, FeatureFlagDefinition>

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS

export const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[]

export const FLAG_DEFAULTS: Record<FeatureFlagKey, boolean> = Object.fromEntries(
  (Object.entries(FEATURE_FLAGS) as [FeatureFlagKey, FeatureFlagDefinition][]).map(([key, def]) => [key, def.default])
) as Record<FeatureFlagKey, boolean>
